// EN-4743. Corrections applied to the browser OCR's output before it reaches
// findAndParseMrz (./mrz.ts).
//
// The kiosk uses Apple Vision; the browser uses Tesseract, which makes different
// mistakes. Each fix below was needed on a real Indian passport in testing:
//   1. Letter/digit swaps in fields ICAO says are numeric: `9O08010` for the birth
//      date `9008010`, which fails its check digit.
//   2. A dropped filler chevron on line 2, which shifts the composite check digit
//      off position 44.
//   3. The document type on line 1 misread (`6<IND...`). Every TD3 passport's line
//      1 starts with `P<`, and the parser requires it.
// Only lines of plausible TD3 length (40-48) are touched, so a truncated fragment
// is never altered further. Line 2 fixes are safe because its check digits still
// have to pass afterwards.
import type { MrzLine } from './mrz';

// TD3 line 2 positions that can only hold a digit: the document-number check
// digit, birth date + check, expiry + check, and the composite.
const DIGIT_POSITIONS = new Set([9, 13, 14, 15, 16, 17, 18, 19, 21, 22, 23, 24, 25, 26, 27, 43]);
// Line 2 nationality code: letters or filler only.
const ALPHA_POSITIONS = new Set([10, 11, 12]);
// Line 1 issuing-country code.
const NAME_LINE_ALPHA_POSITIONS = new Set([2, 3, 4]);

const TO_DIGIT: Record<string, string> = { O: '0', Q: '0', D: '0', U: '0', I: '1', L: '1', T: '1', Z: '2', S: '5', B: '8', G: '6' };
const TO_ALPHA: Record<string, string> = { 0: 'O', 1: 'I', 2: 'Z', 5: 'S', 8: 'B', 6: 'G' };

const TD3_LENGTH = 44;
const HEAD_LENGTH = 28; // document number .. expiry check digit
const TAIL_FIELD_LENGTH = 15; // personal number (14) + its check digit
const MIN_LINE = 40;
const MAX_LINE = 48;

export interface CorrectedMrzLine extends MrzLine {
  // What OCR returned, after character clean-up but before the fixes.
  raw: string;
  fixed: boolean;
}

// Line 1 with any first character: X<CCC, then a name block containing '<<'.
// Position 1 is always filler on line 1; on line 2 it's the second character of
// the document number (numbers are padded at the end), so a data line doesn't
// match. Loose on purpose: a name line with a stray digit must never go through
// the line-2 fixes.
const isNameLineShape = (t: string): boolean => /^[A-Z0-9]<[A-Z0-9<]{3}/.test(t) && t.slice(5).includes('<<');

const cleanText = (text: string | undefined): string =>
  (text ?? '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/[«‹≪]/g, '<')
    .replace(/[^A-Z0-9<]/g, '');

const mapPositions = (t: string, positions: Set<number>, table: Record<string, string>): string =>
  [...t].map((ch, i) => (positions.has(i) && table[ch] ? table[ch] : ch)).join('');

const fixNameLine = (t: string): string => mapPositions(`P${t.slice(1)}`, NAME_LINE_ALPHA_POSITIONS, TO_ALPHA);

// Re-pad the personal-number block so the composite digit lands on position 44.
// Anchored on the last character, which is always the composite check digit.
const realignDataLine = (t: string): string => {
  if (t.length === TD3_LENGTH) return t;
  const head = t.slice(0, HEAD_LENGTH);
  const composite = t.slice(-1);
  let middle = t.slice(HEAD_LENGTH, -1);
  if (middle.length < TAIL_FIELD_LENGTH) {
    middle = middle.slice(0, -1).padEnd(TAIL_FIELD_LENGTH - 1, '<') + middle.slice(-1);
  } else if (middle.length > TAIL_FIELD_LENGTH) {
    middle = middle.slice(0, TAIL_FIELD_LENGTH - 1) + middle.slice(-1);
  }
  return head + middle + composite;
};

const fixDataLine = (t: string): string =>
  mapPositions(mapPositions(realignDataLine(t), DIGIT_POSITIONS, TO_DIGIT), ALPHA_POSITIONS, TO_ALPHA);

export const correctMrzLines = (lines: MrzLine[]): CorrectedMrzLine[] =>
  lines.map((line) => {
    const raw = cleanText(line.text);
    let text = raw;
    if (raw.length >= MIN_LINE && raw.length <= MAX_LINE) {
      text = isNameLineShape(raw) ? fixNameLine(raw) : fixDataLine(raw);
    }
    return { ...line, text, raw, fixed: text !== raw };
  });
